import React from 'react';
import cx from 'classnames';
import { makeStyles } from '@mui/styles';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import PhotoIcon from '@mui/icons-material/PhotoSizeSelectActualOutlined';
import TextInput from 'components/form/TextInput';
import Dropzone from 'components/form/Dropzone';
import VideoIcon from '@mui/icons-material/VideocamOutlined';
import VideocamIcon from '@mui/icons-material/Videocam';
import YouTubeIcon from '@mui/icons-material/YouTube';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import Spin from 'components/Spin';
import Button from 'components/Button';
import TimesIcon from '@mui/icons-material/Cancel';
import IconButton from 'components/IconButton';
import Paper from '@mui/material/Paper';

const useStyles = makeStyles((theme) => ({
  button: {
    'border': '1px solid',
    'borderColor': theme.palette.grey['300'],
    'padding': theme.spacing(1),
    'borderRadius': 12,
    'marginLeft': theme.spacing(0.5),
    'marginRight': theme.spacing(0.5),
    'flex': 1,
    'cursor': 'pointer',
    'display': 'flex',
    'flexDirection': 'column',
    'alignItems': 'center',
    'textAlign': 'center',
    '& span': {
      fontWeight: 500,
      color: 'inherit',
    },
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
    '&.active': {
      borderColor: theme.palette.primary.main,
      backgroundColor: theme.palette.action.selected,
      color: theme.palette.primary.main,
    },
  },
  dashed: {
    borderStyle: 'dashed',
  },
  thumb: {
    width: '100%',
    height: 'auto',
    maxHeight: 100,
    objectFit: 'contain',
  },
}));

const CustomizePage = ({ values, onChange, onRestart, uploadProgress }) => {
  const classes = useStyles();

  const handleChange = (e) => {
    const { name, value } = e.target;
    onChange(name, value);
  };

  const onSourceTypeChange = (type) => {
    onChange('video_source', type);
    onChange('video_url', '');
    onChange('video', '');
  };

  return (
    <Spin spinning={uploadProgress} tip={`Uploading... ${uploadProgress}%`}>
      <Box mt={1}>
        <Stack
          direction="row"
          spacing={1}
          justifyContent="space-between"
          mb={1}
        >
          <Box
            className={cx(classes.button, {
              active: values.video_source === 'camera',
            })}
            onClick={() => onSourceTypeChange('camera')}
          >
            <VideocamIcon />
            <Typography variant="caption">Record</Typography>
          </Box>
          <Box
            className={cx(classes.button, {
              active: values.video_source === 'url',
            })}
            onClick={() => onSourceTypeChange('url')}
          >
            <YouTubeIcon />
            <Typography variant="caption">YouTube or Vimeo</Typography>
          </Box>
          <Box
            className={cx(classes.button, {
              active: values.video_source === 'upload',
            })}
            onClick={() => onSourceTypeChange('upload')}
          >
            <CloudUploadIcon />
            <Typography variant="caption">Upload</Typography>
          </Box>
        </Stack>
        {values.video_source === 'camera' &&
          (values.video_url || values.video) && (
            <Box mb={2}>
              <Button fullWidth onClick={onRestart}>
                Delete And Re-record
              </Button>
            </Box>
          )}
        {values.video_source === 'url' && (
          <Paper variant="outlined" sx={{ p: 2 }}>
            <TextInput
              fullWidth
              label="Enter Video URL"
              name="video_url"
              value={values.video_url}
              onChange={handleChange}
              placeholder="https://www.yourvideo.com"
            />
          </Paper>
        )}
        {values.video_source === 'upload' && (
          <Dropzone
            showPreview={false}
            onChange={(files) => onChange('video', files[0])}
            accept={{
              'video/mp4': ['.mp4'],
            }}
            placeholder={
              <>
                <VideoIcon />
                <Typography gutterBottom fontWeight={600} sx={{ mt: 1 }}>
                  Upload the video
                </Typography>
                <Typography variant="caption">
                  Click here or drag and drop
                </Typography>
              </>
            }
          />
        )}
        {values.video_source !== 'url' && (
          <Box>
            <Dropzone
              margin="none"
              key={values.thumbnail_url}
              onChange={(files) => onChange('cover', files[0])}
              showPreview
              placeholder={
                <>
                  {values.thumbnail_url && !values.cover && (
                    <Box position="relative" mx={2}>
                      <img
                        className={classes.thumb}
                        src={values.thumbnail_url}
                      />
                      <IconButton
                        title="Remove"
                        color="error"
                        icon={<TimesIcon />}
                        onClick={(e) => {
                          e.stopPropagation();
                          onChange('thumbnail_url', null);
                        }}
                        sx={{
                          position: 'absolute',
                          top: -15,
                          right: -15,
                        }}
                      />
                    </Box>
                  )}
                  <>
                    <PhotoIcon />
                    <Typography gutterBottom fontWeight={600} sx={{ mt: 1 }}>
                      Pick a thumbnail for the video cover image.
                    </Typography>
                    <Typography variant="caption">
                      Click here or drag and drop
                    </Typography>
                  </>
                </>
              }
            />
          </Box>
        )}
      </Box>
    </Spin>
  );
};

export default CustomizePage;
